import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import { Save, Lock, Person } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import authService from '../services/authService';
import api from '../services/api';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);

  // Datos del perfil
  const [profileData, setProfileData] = useState({
    fullName: '',
    email: '',
    phone: '',
    dni: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'Argentina',
    birthDate: ''
  });

  // Datos de cambio de contraseña
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (user) {
      setProfileData({
        fullName: user.fullName || '',
        email: user.email || '',
        phone: user.phone || '',
        dni: user.dni || '',
        address: user.address || '',
        city: user.city || '',
        state: user.state || '',
        postalCode: user.postalCode || '',
        country: user.country || 'Argentina',
        birthDate: user.birthDate || ''
      });
    }
  }, [user]);

  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
  };

  const handleUpdateProfile = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.put('/auth/profile', profileData);
      const result = response.data;

      if (result.success) {
        setMessage({ type: 'success', text: 'Perfil actualizado correctamente' });
        // Actualizar el contexto de usuario
        updateUser(result.data);
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error al actualizar el perfil' });
    }

    setLoading(false);
  };

  const handleChangePassword = async () => {
    setMessage({ type: '', text: '' });

    // Validaciones
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Completa todos los campos de contraseña' });
      return;
    }

    if (
      passwordData.newPassword.length < 10
      || !/[a-z]/.test(passwordData.newPassword)
      || !/[A-Z]/.test(passwordData.newPassword)
      || !/\d/.test(passwordData.newPassword)
    ) {
      setMessage({ type: 'error', text: 'Usá al menos 10 caracteres, con mayúscula, minúscula y número' });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Las contraseñas no coinciden' });
      return;
    }

    setLoading(true);

    const result = await authService.changePassword(
      passwordData.currentPassword,
      passwordData.newPassword
    );

    if (result.success) {
      await authService.logout();
      window.location.href = '/login';
      return;
    } else {
      setMessage({ type: 'error', text: result.message });
    }

    setLoading(false);
  };

  const getListaLabel = (lista) => {
    if (!lista) return 'No asignada';
    return `Lista ${lista}`;
  };

  return (
    <Box>
      <Typography variant="h4" className="mb-lg font-bold">
        Mi Perfil
      </Typography>

      {message.text && (
        <Alert 
          severity={message.type} 
          className="mb-md"
          onClose={() => setMessage({ type: '', text: '' })}
        >
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Información del Usuario */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box className="d-flex align-center mb-md">
                <Person color="primary" style={{ marginRight: 8 }} />
                <Typography variant="h6">
                  Información Personal
                </Typography>
              </Box>
              <Divider className="mb-md" />

              <Box className="mb-md">
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Usuario
                </Typography>
                <Typography variant="body1" className="font-bold">
                  {user?.username}
                </Typography>
              </Box>

              <Box className="mb-md">
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Rol
                </Typography>
                <Typography variant="body1" className="font-bold">
                  Cliente
                </Typography>
              </Box>

              <Box className="mb-md">
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Lista de Precios Asignada
                </Typography>
                <Typography variant="body1" className="font-bold">
                  {getListaLabel(user?.precioListaAsignada)}
                </Typography>
              </Box>

              <Divider className="mb-md mb-lg" />

              <Typography variant="subtitle1" className="mb-md font-bold" color="primary">
                Datos Personales
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nombre Completo"
                    value={profileData.fullName}
                    onChange={(e) => handleProfileChange('fullName', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={profileData.email}
                    onChange={(e) => handleProfileChange('email', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Teléfono"
                    value={profileData.phone}
                    onChange={(e) => handleProfileChange('phone', e.target.value)}
                    placeholder="+54 9 11 1234-5678"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="DNI / CUIT"
                    value={profileData.dni}
                    onChange={(e) => handleProfileChange('dni', e.target.value)}
                    placeholder="12345678"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Fecha de Nacimiento"
                    type="date"
                    value={profileData.birthDate}
                    onChange={(e) => handleProfileChange('birthDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              <Typography variant="subtitle1" className="mb-md mt-lg font-bold" color="primary">
                Dirección de Envío
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Dirección"
                    value={profileData.address}
                    onChange={(e) => handleProfileChange('address', e.target.value)}
                    placeholder="Av. Corrientes 1234"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Ciudad"
                    value={profileData.city}
                    onChange={(e) => handleProfileChange('city', e.target.value)}
                    placeholder="Buenos Aires"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Provincia / Estado"
                    value={profileData.state}
                    onChange={(e) => handleProfileChange('state', e.target.value)}
                    placeholder="CABA"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Código Postal"
                    value={profileData.postalCode}
                    onChange={(e) => handleProfileChange('postalCode', e.target.value)}
                    placeholder="C1043"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="País"
                    value={profileData.country}
                    onChange={(e) => handleProfileChange('country', e.target.value)}
                  />
                </Grid>
              </Grid>

              <Button
                fullWidth
                variant="contained"
                startIcon={<Save />}
                onClick={handleUpdateProfile}
                disabled={loading}
                className="mt-lg"
              >
                Guardar Cambios
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Cambio de Contraseña */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box className="d-flex align-center mb-md">
                <Lock color="primary" style={{ marginRight: 8 }} />
                <Typography variant="h6">
                  Cambiar Contraseña
                </Typography>
              </Box>
              <Divider className="mb-md" />

              <TextField
                fullWidth
                label="Contraseña Actual"
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                className="mb-md"
              />

              <TextField
                fullWidth
                label="Nueva Contraseña"
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                className="mb-md"
                helperText="Mínimo 6 caracteres"
              />

              <TextField
                fullWidth
                label="Confirmar Nueva Contraseña"
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                className="mb-lg"
              />

              <Button
                fullWidth
                variant="contained"
                color="secondary"
                startIcon={<Lock />}
                onClick={handleChangePassword}
                disabled={loading}
              >
                Cambiar Contraseña
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Profile;

